import { useRef, useState } from "react";
import {
    IonButton,
    IonCard,
    IonCardContent,
    IonCardHeader,
    IonCardTitle,
    IonCol,
    IonGrid,
    IonLabel,
    IonRow,
    IonSelectOption,
    IonText
} from "@ionic/react";
import { format, parseISO } from "date-fns";
import pt_BR from "date-fns/locale/pt-BR";
import Calendar from "react-calendar";

import { useToast } from "../../../hooks/toast";
import { useValidation } from "../../../hooks/validation";

import { SubmitHandler, FormHandles } from "@unform/core"
import { Form } from "@unform/web"
import api from "../../../services/api"
import { AxiosRequestConfig } from "axios"

import Main from "../../../components/Main"
import Card from "../../../components/Card"
import Select from "../../../components/Select";
import Textarea from "../../../components/Textarea";
import Input from "../../../components/Input";
import ReactSelect from "../../../components/ReactSelect";

const sessionName = process.env.REACT_APP_SESSION_NAME

interface Lines {
    id: number
    technician: string
    date: string
    medical_statement: "N" | "Y"
    days: number
    note: string
    created_at: string
    edit: "Y" | "N"
    delete: "Y" | "N"
}

const EnfermagemTecnicoFalta = () => {
    const [scroll, setScroll] = useState(new Date())
    const { getValidationErrors } = useValidation()
    const { successToast } = useToast()
    const formRef = useRef<FormHandles>(null)
    const [id, setId] = useState<number>(0)
    const consultRef = useRef<FormHandles>(null)
    const [month, setMonth] = useState<string>("")
    const [total, setTotal] = useState<number>(0)
    const [days, setDays] = useState<string[]>([])
    const [rows, setRows] = useState<Lines[]>([])

    const formDefaultValues = () => {
        return {
            days: 1,
            medical_statement: "N"
        }
    }

    const consultDefaultValues = () => {
        return JSON.parse(
            `${localStorage.getItem(
                `@${sessionName}:NurseTechniciansAbsences_ConsultForm`
            ) || "{}"
            }`
        )
    }

    const getList = async () => {
        const data = consultDefaultValues()

        await api
            .get("enfermagem/tecnico-falta", {
                params: {
                    technician_id: data.technician?.value,
                    month: data.month
                }
            })
            .then(response => {
                const { month, total, days, data } = response.data

                if (month) {
                    setMonth(month)
                }

                if (total) {
                    setTotal(total)
                }

                if (days) {
                    setDays(days)
                }

                if (data) {
                    setRows(data)
                }
            })
            .catch(error => {
                getValidationErrors(error.response)
            })
    }

    const handleReset = () => {
        formRef.current?.reset()
        setId(0)
    }

    const handleSubmit: SubmitHandler = async data => {
        let method: AxiosRequestConfig["method"] = "post"
        if (id > 0) {
            method = "put"
        }
        await api({
            method: method,
            url: `enfermagem/tecnico-falta${id > 0 ? "/" + id : ""}`,
            data: data
        })
            .then(_response => {
                successToast()
                handleReset()
                if (total > 0) {
                    getList()
                }
            })
            .catch(error => {
                getValidationErrors(error.response)
            })
    }

    const handleConsult: SubmitHandler = async data => {
        localStorage.setItem(
            `@${sessionName}:NurseTechniciansAbsences_ConsultForm`,
            JSON.stringify(data)
        )
        getList()
    }

    const handleEdit = async (id: number) => {
        await api
            .get(`enfermagem/tecnico-falta/${id}`)
            .then(response => {
                setId(id)
                formRef.current?.setData(response.data)
                setScroll(new Date())
            })
            .catch(error => {
                getValidationErrors(error.response)
            })
    }

    const handleDelete = async (id: number) => {
        await api.delete(`enfermagem/tecnico-falta/${id}`).then(response => {
            successToast()
            getList()
        })
    }

    return (
        <Main title="Técnicos (Faltas)" scroll={scroll}>
            <Card>
                <IonCardContent>
                    <IonGrid>
                        <Form
                            ref={formRef}
                            initialData={formDefaultValues()}
                            onSubmit={handleSubmit}
                        >
                            <IonRow className="ion-align-items-end">
                                <ReactSelect
                                    label="Técnico"
                                    name="technician_id"
                                    url="/enfermagem/select/tecnico"
                                    params={{ status: "A" }}
                                    required
                                    simple
                                />
                                <Input
                                    name="date"
                                    label="Data"
                                    type="date"
                                    max={format(new Date(), "yyyy-MM-dd")}
                                    required
                                />
                                <Select name="medical_statement" label="Atestado">
                                    <IonSelectOption value="N">Não</IonSelectOption>
                                    <IonSelectOption value="Y">Sim</IonSelectOption>
                                </Select>
                                <Select name="days" label="Quantidade de Dias que Faltou">
                                    {(function (i, len) {
                                        let rows = []
                                        while (++i <= len) {
                                            rows.push(
                                                <IonSelectOption key={i} value={i}>
                                                    {i}
                                                </IonSelectOption>
                                            )
                                        }
                                        return rows
                                    })(0, 20)}
                                </Select>
                                <Textarea name="note" label="Descrição" />
                                <IonCol size="6" className="ion-margin-top">
                                    <IonButton type="submit" color="primary" expand="block">
                                        Salvar
                                    </IonButton>
                                </IonCol>
                                {id > 0 && (
                                    <IonCol size="6" className="ion-margin-top">
                                        <IonButton
                                            type="button"
                                            color="medium"
                                            expand="block"
                                            onClick={() => handleReset()}
                                        >
                                            Cancelar
                                        </IonButton>
                                    </IonCol>
                                )}
                            </IonRow>
                        </Form>
                    </IonGrid>
                </IonCardContent>
            </Card>
            <IonCard>
                <IonCardContent>
                    <IonGrid>
                        <Form
                            ref={consultRef}
                            initialData={consultDefaultValues()}
                            onSubmit={handleConsult}
                        >
                            <IonRow className="ion-align-items-end">
                                <ReactSelect
                                    label="Técnico"
                                    name="technician"
                                    url="/enfermagem/select/tecnico"
                                    params={{ status: "A" }}
                                    required
                                />
                                <Input
                                    name="month"
                                    label="Mês"
                                    type="month"
                                    max={format(new Date(), "yyyy-MM")}
                                />
                            </IonRow>
                            <IonRow className="ion-margin-top">
                                <IonCol size="6">
                                    <IonButton type="submit" color="primary" expand="block">
                                        Consultar
                                    </IonButton>
                                </IonCol>
                            </IonRow>
                        </Form>
                    </IonGrid>
                </IonCardContent>
            </IonCard>
            {rows.length > 0 && (
                <>
                    <IonCard>
                        <IonCardContent>
                            <IonRow>
                                <IonCol size="12">
                                    <IonLabel>FALTAS: </IonLabel>
                                    <IonText>{total}</IonText>
                                </IonCol>
                                {days.length > 0 && (
                                    <IonCol size="12">
                                        <IonLabel>
                                            {format(parseISO(month + '-01'), "MMMM 'de' yyyy", {
                                                locale: pt_BR
                                            })}
                                        </IonLabel>
                                        <Calendar
                                            locale="pt-BR"
                                            onChange={() => { }}
                                            activeStartDate={parseISO(month + "-01")}
                                            tileClassName={props => {
                                                const date = props.date
                                                if (days.includes(format(date, "yyyy-MM-dd"))) {
                                                    return "react-calendar__tile--active"
                                                } else {
                                                    return ""
                                                }
                                            }}
                                            showNavigation={false}
                                            showNeighboringMonth={false}
                                            view="month"
                                        />
                                    </IonCol>
                                )}
                            </IonRow>
                        </IonCardContent>
                    </IonCard>
                    {rows.map(lines => {
                        return (
                            <IonCard key={lines.id}>
                                <IonCardHeader>
                                    <IonCardTitle>
                                        <IonRow>
                                            <IonCol size="12">
                                                <IonLabel>DATA: </IonLabel>
                                                <IonText>
                                                    {format(parseISO(lines.date), "dd/MM/yyyy")}
                                                </IonText>
                                            </IonCol>
                                        </IonRow>
                                    </IonCardTitle>
                                </IonCardHeader>
                                <IonCardContent>
                                    <IonRow>
                                        <IonCol size="12">
                                            <IonLabel>TÉCNICO: </IonLabel>
                                            <IonText>{lines.technician}</IonText>
                                        </IonCol>
                                        <IonCol size="12">
                                            <IonLabel>ATESTADO: </IonLabel>
                                            <IonText>
                                                {lines.medical_statement === "Y" ? "SIM" : "NÃO"}
                                            </IonText>
                                        </IonCol>
                                        <IonCol size="12">
                                            <IonLabel>DIAS: </IonLabel>
                                            <IonText>{lines.days}</IonText>
                                        </IonCol>
                                        {!!lines.note && (
                                            <IonCol size="12">
                                                <IonLabel>DESCRIÇÃO: </IonLabel>
                                                <IonText>{lines.note}</IonText>
                                            </IonCol>
                                        )}
                                        <IonCol size="12">
                                            <IonLabel>CRIADO EM: </IonLabel>
                                            <IonText>
                                                {format(parseISO(lines.created_at), "dd/MM/yyyy")}
                                            </IonText>
                                        </IonCol>
                                    </IonRow>
                                    <IonRow>
                                        {lines.edit === "Y" && (
                                            <IonCol size="6">
                                                <IonButton
                                                    type="button"
                                                    color="primary"
                                                    expand="block"
                                                    onClick={() => handleEdit(lines.id)}
                                                >
                                                    Editar
                                                </IonButton>
                                            </IonCol>
                                        )}
                                        {lines.delete === "Y" && (
                                            <IonCol size="6">
                                                <IonButton
                                                    type="button"
                                                    color="danger"
                                                    expand="block"
                                                    onClick={() => handleDelete(lines.id)}
                                                >
                                                    Deletar
                                                </IonButton>
                                            </IonCol>
                                        )}
                                    </IonRow>
                                </IonCardContent>
                            </IonCard>
                        )
                    })}
                </>
            )}
        </Main>
    )
}

export default EnfermagemTecnicoFalta
